/**
 * Per-stage "on now / next" derivation for map stage cards (handoff §3).
 *
 * Input rows come from the event_lineup() RPC: stage, performance_day (date),
 * set_start_time / set_end_time (time-of-day). Times are interpreted in the
 * DEVICE's timezone — the person reading the map is standing at the festival,
 * so device-local and venue-local agree; anyone browsing from another timezone
 * gets approximate now/next, which is acceptable for a glanceable card.
 *
 * A festival with no set times (Ocean Way today) yields empty schedules and
 * every stage renders idle — visibly quieter than a live one, per the design.
 */

export type LineupRow = {
  artist_name: string;
  billing_name?: string | null;
  stage: string | null;
  performance_day: string | null; // 'YYYY-MM-DD'
  set_start_time: string | null; // 'HH:MM:SS'
  set_end_time: string | null;
};

export type StageSet = {
  name: string;
  startMs: number;
  endMs: number | null;
};

export type StageStatus = {
  /** Set on stage right now, if any. */
  current: StageSet | null;
  /** Next set to start, if any. */
  next: StageSet | null;
};

function toMs(day: string, time: string): number | null {
  const ms = new Date(`${day}T${time}`).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * End of a set with no listed end time: assume a slot. Festival sets run
 * 45-90 minutes; an hour keeps "on now" honest without inventing precision.
 */
const DEFAULT_SET_MS = 60 * 60 * 1000;

/** Sets after 00:00 belong to the previous performance_day's night. A set
 * starting before this hour is shifted forward a day. */
const LATE_NIGHT_CUTOFF_HOUR = 6;

export function buildStageSchedules(rows: LineupRow[]): Map<string, StageSet[]> {
  const byStage = new Map<string, StageSet[]>();
  for (const row of rows) {
    if (!row.stage || !row.performance_day || !row.set_start_time) continue;
    let startMs = toMs(row.performance_day, row.set_start_time);
    if (startMs == null) continue;
    const hour = Number(row.set_start_time.slice(0, 2));
    if (Number.isFinite(hour) && hour < LATE_NIGHT_CUTOFF_HOUR) {
      startMs += 24 * 60 * 60 * 1000;
    }
    let endMs: number | null = null;
    if (row.set_end_time) {
      endMs = toMs(row.performance_day, row.set_end_time);
      if (endMs != null && endMs <= startMs) endMs += 24 * 60 * 60 * 1000;
    }
    const list = byStage.get(row.stage) ?? [];
    list.push({
      name: row.billing_name || row.artist_name,
      startMs,
      endMs,
    });
    byStage.set(row.stage, list);
  }
  for (const list of byStage.values()) {
    list.sort((a, b) => a.startMs - b.startMs);
  }
  return byStage;
}

export function stageStatusAt(
  schedules: Map<string, StageSet[]>,
  lineupStageName: string | null,
  nowMs: number,
): StageStatus {
  const sets = lineupStageName ? schedules.get(lineupStageName) : null;
  if (!sets?.length) return { current: null, next: null };
  let current: StageSet | null = null;
  let next: StageSet | null = null;
  for (const set of sets) {
    const end = set.endMs ?? set.startMs + DEFAULT_SET_MS;
    if (set.startMs <= nowMs && nowMs < end) current = set;
    else if (set.startMs > nowMs && (!next || set.startMs < next.startMs)) next = set;
  }
  return { current, next };
}

/** Progress through the current set, 0..1, for the card's progress fill. */
export function setProgress(set: StageSet, nowMs: number): number {
  const end = set.endMs ?? set.startMs + DEFAULT_SET_MS;
  if (end <= set.startMs) return 0;
  return Math.min(1, Math.max(0, (nowMs - set.startMs) / (end - set.startMs)));
}

/** "8:40" — the card's compact next-set time. */
export function shortTime(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours() % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** One set in a stage's night, with the lineup row it came from (the map's stage preview lists these). */
export type NightSet<R> = { row: R; startMs: number; endMs: number; live: boolean };

/**
 * What a stage has left (Sam, 2026-10-06: tap a stage on the map, see its sets like the Sets page column): the night that
 * is on now or comes next - a night is a performance_day, sets past midnight included - and its sets that have not ended.
 * Before the festival that is the first night, whole. Null when the stage has nothing left.
 */
export function stageNight<R extends LineupRow>(
  rows: R[],
  stage: string | null,
  nowMs: number,
): { day: string; sets: NightSet<R>[] } | null {
  if (!stage) return null;
  const byDay = new Map<string, NightSet<R>[]>();
  for (const row of rows) {
    if (row.stage !== stage || !row.performance_day || !row.set_start_time) continue;
    let startMs = toMs(row.performance_day, row.set_start_time);
    if (startMs == null) continue;
    const hour = Number(row.set_start_time.slice(0, 2));
    if (Number.isFinite(hour) && hour < LATE_NIGHT_CUTOFF_HOUR) startMs += 24 * 60 * 60 * 1000;
    let endMs = row.set_end_time ? toMs(row.performance_day, row.set_end_time) : null;
    if (endMs != null && endMs <= startMs) endMs += 24 * 60 * 60 * 1000;
    const end = endMs ?? startMs + DEFAULT_SET_MS;
    const day = row.performance_day.slice(0, 10);
    const list = byDay.get(day) ?? [];
    // a b2b is one set: the lineup has a row per artist, each billed as the pair (Seth Troxler b2b Ben Sterling, twice)
    if (
      list.some(
        (s) =>
          s.startMs === startMs &&
          (s.row.billing_name || s.row.artist_name) === (row.billing_name || row.artist_name),
      )
    )
      continue;
    list.push({ row, startMs, endMs: end, live: startMs <= nowMs && nowMs < end });
    byDay.set(day, list);
  }
  for (const day of [...byDay.keys()].sort()) {
    const sets = byDay
      .get(day)!
      .filter((s) => s.endMs > nowMs)
      .sort((a, b) => a.startMs - b.startMs);
    if (sets.length) return { day, sets };
  }
  return null;
}
